import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Badge,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  EventSeat as SeatIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import { toast } from 'react-toastify';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    capacity: '',
    price: '',
    status: 'UPCOMING',
    images: [],
  });

  /* ================= FETCH EVENTS ================= */
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get('/events');
      setEvents(res.data);
    } catch {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  /* ================= DIALOG HANDLING ================= */
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      location: '',
      capacity: '',
      price: '',
      status: 'UPCOMING',
      images: [],
    });
  };

  const handleOpenDialog = (type, event = null) => {
    setDialogType(type);
    setSelectedEvent(event);

    if (type === 'edit' && event) {
      setFormData({
        title: event.title,
        description: event.description,
        date: new Date(event.date).toISOString().split('T')[0],
        location: event.location,
        capacity: event.capacity,
        price: event.price,
        status: event.status,
        images: event.images || [],
      });
    } else {
      resetForm();
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
    resetForm();
  };

  const handleOpenViewDialog = (event) => {
    setSelectedEvent(event);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedEvent(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingImage(true);

    try {
      const uploadData = new FormData();
      files.forEach(file => uploadData.append('images', file));

      const res = await axios.post('/upload/multiple', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!res.data?.success || !Array.isArray(res.data.data)) {
        throw new Error('Invalid upload response');
      }

      setFormData(prev => {
        const newImages = res.data.data.map((img, index) => ({
          url: img.url,
          filename: img.filename,
          isPrimary: prev.images.length === 0 && index === 0,
        }));

        return {
          ...prev,
          images: [...prev.images, ...newImages],
        };
      });

      toast.success('Images uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => {
      const images = [...prev.images];
      const wasPrimary = images[index]?.isPrimary;
      images.splice(index, 1);

      if (wasPrimary && images.length) {
        images[0].isPrimary = true;
      }

      return { ...prev, images };
    });
  };

  const handleSetPrimaryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    }));
  };

  /* ================= SAVE EVENT ================= */
  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        capacity: Number(formData.capacity),
        price: Number(formData.price),
      };

      if (dialogType === 'add') {
        await axios.post('/events', payload);
        toast.success('Event created');
      } else {
        await axios.put(`/events/${selectedEvent._id}`, payload);
        toast.success('Event updated');
      }

      handleCloseDialog();
      fetchEvents();
    } catch {
      toast.error('Failed to save event');
    }
  };

  /* ================= HELPERS ================= */
  const getEventImage = (event) => {
    if (event.images?.length) {
      const primary = event.images.find(i => i.isPrimary);
      return primary?.url || event.images[0].url;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'UPCOMING':
        return { bg: '#E3F2FD', text: '#1976D2' };
      case 'ONGOING':
        return { bg: '#E8F5E9', text: '#388E3C' };
      case 'COMPLETED':
        return { bg: '#F3E5F5', text: '#7B1FA2' };
      case 'CANCELLED':
        return { bg: '#FFEBEE', text: '#C62828' };
      default:
        return { bg: '#E0E0E0', text: '#616161' };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  /* ================= UI ================= */
  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Events Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Create Event
        </Button>
      </Box>

      <Grid container spacing={3}>
        {events.map(event => (
          <Grid item xs={12} key={event._id}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                transition: '0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 35px rgba(0,0,0,0.12)',
                },
              }}
            >
              {/* IMAGE / VIOLET SECTION */}
              <Box
                sx={{
                  height: 180,
                  backgroundImage: getEventImage(event)
                    ? `url(${getEventImage(event)})`
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                }}
              >
                <Chip
                  label={event.status}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: '#E8F5E9',
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* CONTENT */}
              <CardContent>
                <Typography variant="h6" fontWeight={600}>
                  {event.title}
                </Typography>

                <Box display="flex" gap={3} my={1}>
                  <Box display="flex" gap={1} alignItems="center">
                    <CalendarIcon fontSize="small" />
                    <Typography variant="body2">
                      {new Date(event.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <LocationIcon fontSize="small" />
                    <Typography variant="body2">{event.location}</Typography>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <PeopleIcon fontSize="small" />
                    <Typography variant="body2">
                      {event.registrations?.length || 0} attendees
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={2} mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog('edit', event)}
                  >
                    Edit
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => handleOpenViewDialog(event)}
                  >
                    View
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ================= EDIT/ADD DIALOG ================= */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'add' ? 'Create Event' : 'Edit Event'}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" name="title" value={formData.title} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" name="description" value={formData.description} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" name="date" value={formData.date} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Location" name="location" value={formData.location} onChange={handleInputChange} />
            </Grid>

            {/* IMAGE UPLOAD */}
            <Grid item xs={12}>
              <Box
                component="label"
                sx={{
                  border: '2px dashed #ccc',
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <input hidden type="file" multiple accept="image/*" onChange={handleImageUpload} />
                {uploadingImage ? <CircularProgress /> : <UploadIcon />}
              </Box>
            </Grid>

            {/* IMAGE PREVIEW */}
            {formData.images.length > 0 && (
              <Grid item xs={12}>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {formData.images.map((img, i) => (
                    <Badge
                      key={i}
                      badgeContent={
                        <IconButton size="small" onClick={() => handleRemoveImage(i)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <img
                        src={img.url}
                        alt=""
                        width={120}
                        height={120}
                        style={{
                          objectFit: 'cover',
                          border: img.isPrimary ? '3px solid #5B67F1' : '2px solid #ccc',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSetPrimaryImage(i)}
                      />
                    </Badge>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= VIEW DIALOG ================= */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        {selectedEvent && (
          <>
            {/* Hero Image Section */}
            <Box
              sx={{
                height: 280,
                backgroundImage: getEventImage(selectedEvent)
                  ? `url(${getEventImage(selectedEvent)})`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                p: 3,
              }}
            >
              {/* Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)',
                }}
              />
              
              {/* Close Button */}
              

              {/* Status Badge */}
              <Chip
                label={selectedEvent.status}
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  bgcolor: getStatusColor(selectedEvent.status).bg,
                  color: getStatusColor(selectedEvent.status).text,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              />

              {/* Title Overlay */}
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  position: 'relative',
                  zIndex: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {selectedEvent.title}
              </Typography>
            </Box>

            <DialogContent sx={{ p: 4 }}>
              {/* Quick Info Grid */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#F5F7FA',
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#667eea', mx: 'auto', mb: 1 }}>
                      <CalendarIcon />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#F5F7FA',
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#764ba2', mx: 'auto', mb: 1 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Attendees
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedEvent.registrations?.length || 0} / {selectedEvent.capacity}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#F5F7FA',
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#48BB78', mx: 'auto', mb: 1 }}>
                      <MoneyIcon />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Price
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${selectedEvent.price}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#F5F7FA',
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#F56565', mx: 'auto', mb: 1 }}>
                      <SeatIcon />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Available
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedEvent.capacity - (selectedEvent.registrations?.length || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Location Section */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Location
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" pl={4}>
                  {selectedEvent.location}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Description Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  About this Event
                </Typography>
                <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                  {selectedEvent.description}
                </Typography>
              </Box>

              {/* Image Gallery */}
              {selectedEvent.images && selectedEvent.images.length > 1 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      Gallery
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      {selectedEvent.images.map((img, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: img.isPrimary ? '3px solid #667eea' : '2px solid #E0E0E0',
                          }}
                        >
                          <img
                            src={img.url}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: '#F9FAFB' }}>
              <Button 
                onClick={handleCloseViewDialog}
                variant="outlined"
                size="large"
              >
                Close
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<EditIcon />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleOpenDialog('edit', selectedEvent);
                }}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Edit Event
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EventManagement;